import { createFileRoute, Link } from "@tanstack/react-router";
import { GraduationCap, CheckCircle2, BookOpen, Mail, Lock } from "lucide-react";
import {
  LLB_MODULES,
  LLB_MODULE_PRICE_ZAR,
  LLB_BULK_DISCOUNT_THRESHOLD,
  LLB_BULK_DISCOUNT_RATE,
} from "@/lib/llb-modules";
import { Disclaimer } from "@/components/site/Disclaimer";

export const Route = createFileRoute("/education/llb-tutoring")({
  head: () => ({
    meta: [
      { title: "UNISA LLB Tutoring — Apply, Pay, Pass | Nkadimeng Danny" },
      {
        name: "description",
        content:
          "Tutored classes for every UNISA LLB module (Levels 1–4). R150 per module per semester, 10% off when you register 4+ modules. Pre-loaded study notes and daily AI tips by email.",
      },
    ],
  }),
  component: LandingPage,
});

function LandingPage() {
  const byLevel: Record<string, typeof LLB_MODULES> = { "1": [], "2": [], "3": [], "4": [] };
  for (const m of LLB_MODULES) byLevel[String(m.level)].push(m);

  return (
    <div className="container-narrow py-12 space-y-6">
      <Disclaimer variant="compact" />
      <div className="rounded-2xl border border-border bg-navy text-navy-foreground p-8">
        <div className="flex items-center gap-3 text-gold">
          <GraduationCap className="h-6 w-6" />
          <span className="text-xs uppercase tracking-[0.32em]">UNISA LLB tutoring</span>
        </div>
        <h1 className="mt-4 font-display text-3xl md:text-4xl">
          Pass every UNISA LLB module with structured, paid tutored support.
        </h1>
        <p className="mt-3 max-w-2xl text-sm text-navy-foreground/80">
          Apply, pay <strong>R{LLB_MODULE_PRICE_ZAR}/module per semester</strong>, and join tutored classes
          for any Level 1–4 module. Register{" "}
          <strong>{LLB_BULK_DISCOUNT_THRESHOLD}+ modules</strong> in one semester and get{" "}
          <strong>{Math.round(LLB_BULK_DISCOUNT_RATE * 100)}% off</strong> automatically.
        </p>
        <ul className="mt-5 grid gap-2 text-sm text-navy-foreground/90 md:grid-cols-2">
          <li className="flex items-start gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 text-gold" /> Pre-loaded tutor notes & study plan per module</li>
          <li className="flex items-start gap-2"><Mail className="mt-0.5 h-4 w-4 text-gold" /> Daily AI-generated study tips by email</li>
          <li className="flex items-start gap-2"><BookOpen className="mt-0.5 h-4 w-4 text-gold" /> Past-paper drills and exam-pack guidance</li>
          <li className="flex items-start gap-2"><Lock className="mt-0.5 h-4 w-4 text-gold" /> Instant enrolment once PayFast confirms payment</li>
        </ul>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link
            to="/portal/llb-tutoring"
            className="inline-flex items-center justify-center gap-2 rounded-md bg-gold px-4 py-2.5 text-sm font-semibold text-navy hover:brightness-105"
          >
            Select modules & pay
          </Link>
          <Link
            to="/auth"
            className="inline-flex items-center justify-center gap-2 rounded-md border border-navy-foreground/40 px-4 py-2.5 text-sm text-navy-foreground hover:bg-navy-foreground/10"
          >
            Create an account
          </Link>
        </div>
      </div>

      {/* Catalog preview */}
      <section className="mt-10 space-y-6">
        {(["1", "2", "3", "4"] as const).map((lvl) => (
          <div key={lvl} className="rounded-xl border border-border bg-card p-5">
            <h2 className="font-display text-lg text-navy">Level {lvl} modules we tutor</h2>
            <ul className="mt-3 grid gap-1.5 text-sm md:grid-cols-2">
              {byLevel[lvl].map((m) => (
                <li key={m.code} className="flex items-start gap-2">
                  <span className="font-mono text-xs font-semibold text-navy">{m.code}</span>
                  <span className="text-foreground/80">{m.title}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </section>
    </div>
  );
}
